import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SettlementSummary, SettlementTableData } from 'src/app/models/settlement.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { OrganizationTeamApiService } from 'src/app/services/organization-team-api.service';
import { SettlementService } from 'src/app/services/settlement.service';

@Component({
  selector: 'app-settlement-list',
  templateUrl: './settlement-list.component.html',
  styleUrls: ['./settlement-list.component.scss']
})
export class SettlementListComponent implements OnInit {
  settlements: SettlementTableData[] = [];
  summary: SettlementSummary = {
    totalSettlements: 0,
    transactionsSettled: 0,
    successful: 0,
    pending: 0,
    failed: 0,
  };
  count: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;

  dateFrom: string = '';
  dateTo: string = '';

  settlementStatusItems = [
    { id: 1, name: 'Pending', checked: false, translation: 'COMMON.SETTLEMENT_STATUS.PENDING' },
    { id: 2, name: 'Successful', checked: false, translation: 'COMMON.SETTLEMENT_STATUS.SUCCESSFUL' },
    { id: 3, name: 'Failed', checked: false, translation: 'COMMON.SETTLEMENT_STATUS.FAILED' },
    { id: 4, name: 'Unknown', checked: false, translation: 'COMMON.SETTLEMENT_STATUS.UNKNOWN' },
  ];
  selectedStatuses: number[] = [];

  organizations: { id: number; name: string; checked: boolean }[] = [];
  selectedOrganizations: number[] = [];

  clicked: boolean = false;
  selectedFilters: ChecklistItem[] = [];

  constructor(
    private toastr: ToastrService,
    private settlementService: SettlementService,
    private organizationTeamService: OrganizationTeamApiService,
  ) { }

  ngOnInit(): void {
    this.setFilters();
    this.getOrganizations();
    this.reloadTable(null, true);
  }

  pageChange(page: number): void {
    this.currentPage = page;
    this.reloadTable();
  }

  // refreshSummary is true only when the filtered dataset changes (filters), not for
  // pagination which doesn't alter the set the cards aggregate over.
  reloadTable(page: any = null, refreshSummary: boolean = false): void {
    if (page) this.currentPage = page;
    const obj = {
      pageInfo: { page: this.currentPage, pageSize: this.pageSize },
      filterParams: this.getFilterObject()
    };
    this.getAll(obj);
    if (refreshSummary) this.loadSummary(obj);
  }

  selectStatus(): void {
    this.createFilterCards();
  }

  selectVendor(): void {
    this.createFilterCards();
  }

  applyFilter(): void {
    this.reloadTable(1, true);
  }

  resetFilter(): void {
    this.selectedFilters = [];
    this.dateFrom = '';
    this.dateTo = '';
    this.selectedStatuses = [];
    this.selectedOrganizations = [];

    this.settlementStatusItems = this.settlementStatusItems.map(s => ({ ...s, checked: false }));
    this.settlementStatusItems = [...this.settlementStatusItems];

    this.organizations = this.organizations.map(o => ({ ...o, checked: false }));
    this.organizations = [...this.organizations];

    this.reloadTable(1, true);
  }

  removeFilter(item: ChecklistItem): void {
    if (item.property == 'Settlement Status') {
      this.settlementStatusItems.forEach(s => {
        if (s.name === item.selectedValue) s.checked = false;
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Map Vendor') {
      this.organizations.forEach(o => {
        if (o.name === item.selectedValue) o.checked = false;
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Date From') {
      this.dateFrom = '';
    } else if (item.property == 'Date To') {
      this.dateTo = '';
    }

    this.createFilterCards();
    if (this.selectedFilters.length === 0) this.reloadTable(1, true);
  }

  createFilterCards(): void {
    this.selectedFilters = [];

    const selectedStatuses = this.settlementStatusItems.filter(x => x.checked);
    this.selectedStatuses = selectedStatuses.map(x => x.id);

    const selectedOrgs = this.organizations.filter(x => x.checked);
    this.selectedOrganizations = selectedOrgs.map(x => x.id);

    for (const s of selectedStatuses) {
      this.selectedFilters.push(new ChecklistItem('Settlement Status', s.name, 'COMMON.SETTLEMENT_STATUS.SINGLE', s.translation));
    }

    for (const o of selectedOrgs) {
      this.selectedFilters.push(new ChecklistItem('Map Vendor', o.name, 'SURVEYS.MAP_VENDOR'));
    }

    if (this.dateFrom) {
      this.selectedFilters.push(new ChecklistItem('Date From', this.dateFrom, 'COMMON.DATE_FROM'));
    }

    if (this.dateTo) {
      this.selectedFilters.push(new ChecklistItem('Date To', this.dateTo, 'COMMON.DATE_TO'));
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Successful': return 'approved-building';
      case 'Pending': return 'submitted-building';
      case 'Failed': return 'rejected-building';
      default: return 'assigned-building';
    }
  }

  getStatusKey(status: string): string {
    switch (status) {
      case 'Successful': return 'COMMON.SETTLEMENT_STATUS.SUCCESSFUL';
      case 'Pending': return 'COMMON.SETTLEMENT_STATUS.PENDING';
      case 'Failed': return 'COMMON.SETTLEMENT_STATUS.FAILED';
      default: return 'COMMON.SETTLEMENT_STATUS.UNKNOWN';
    }
  }

  private getFilterObject(): any {
    return {
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      statuses: this.selectedStatuses.length > 0 ? this.selectedStatuses : null,
      organizationIds: this.selectedOrganizations.length > 0 ? this.selectedOrganizations : null,
    };
  }

  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject()
    };

    // Remove 'T23:59:59' from the dateTo to avoid errors
    if (obj.filterParams.dateTo) {
      obj.filterParams.dateTo = obj.filterParams.dateTo.replace(
        'T23:59:59',
        ''
      );
    }

    localStorage.setItem(
      'filterSettlement',
      JSON.stringify({ type: 'filterSettlement', filter: obj })
    );
  }

  setFilters() {
    var filter = localStorage.getItem('filterSettlement');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'filterSettlement') return;

    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;

    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;

    this.selectedStatuses = filterObject.filter.filterParams.statuses
      ? filterObject.filter.filterParams.statuses
      : [];
    this.selectedOrganizations = filterObject.filter.filterParams.organizationIds
      ? filterObject.filter.filterParams.organizationIds
      : [];

    this.settlementStatusItems.forEach((status) => {
      if (this.selectedStatuses.includes(status.id)) status.checked = true;
    });

    this.organizations.forEach((org) => {
      if (this.selectedOrganizations.includes(org.id)) org.checked = true;
    });

    this.createFilterCards();
    localStorage.removeItem('filterSettlement');
  }

  private getAll(obj: any): void {
    this.settlements = [];
    this.count = 0;
    this.settlementService.getAll(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.settlements = data.data.data;
          this.count = data.data.count;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  private loadSummary(obj: any): void {
    this.settlementService.getSummary(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.summary = data.data;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  private getOrganizations(): void {
    this.organizationTeamService.getAllForSelect().subscribe({
      next: (response) => {
        this.organizations = response.data.map((item: any) => ({
          id: item.id,
          name: item.name || '',
          checked: false,
        }));
      },
      error: (_) => this.toastr.error('Error occurred while fetching organizations'),
    });
  }
}
